import { ActionButton, FormInputRow, Input } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { FormControl, FormControlLabel, FormHelperText, Grid, InputLabel, MenuItem, Radio, RadioGroup, Select } from '@mui/material'
import React from 'react'
import { Controller } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { BannerStatic } from '../../../components'
import { DateInput } from '../../../components/input'
import { CampaignEditSlice } from '../../../reducers'
import { AudienceOptions } from '../../../reducers/campaign-edit/slice'
import { useCampaignFormContext } from '../campaign-form-context'
import { useStyles } from './style'
import addDays from 'date-fns/addDays'
import parseISO from 'date-fns/parseISO'
import isAfter from 'date-fns/isAfter'

const DetailTab = () => {
  const classes = useStyles()
  const {
    form,
    isEdit,
    mainClasses,
    setCurrentTabValidation,
    campaign
  } = useCampaignFormContext()

  const dispatch = useDispatch()

  const { formState: { errors }, setValue, trigger, watch, getValues } = form

  React.useEffect(() => {
    setCurrentTabValidation(() => () => trigger(['name', 'priority', 'startDate', 'endDate', 'audience', 'landingPage']))
  }, [setCurrentTabValidation, trigger])

  const [campaignSetDefault] = useEvent([
    {
      eventName: 'campaignSetDefault',
      onSuccess: () => {
        BannerStatic.show({ label: 'Campaign set to default' })
        dispatch(CampaignEditSlice.actions.setCampaignDefault())
      },
      onError: (err) => {
        console.error('campaignSetDefault.onError', err)
        BannerStatic.show({ label: 'There was an error updating the Campaign', isError: true })
      }
    }
  ])

  const [priority, startDate] = watch(['priority', 'startDate'])

  const minEndDate = React.useMemo(() => {
    if (!startDate) return
    return addDays(parseISO(startDate), 1)
  }, [startDate])

  const handleChangePriority = React.useCallback((ev, field) => {
    field.onChange(ev)
    const { value } = ev.target
    if (value === 'default') {
      setValue('startDate', undefined)
      setValue('endDate', undefined)
      setValue('audience', undefined)
    }
  }, [setValue])

  const handlePushLive = React.useCallback(() => {
    const { pk, sk } = campaign
    campaignSetDefault.trigger({ pk, sk, isDefault: true })
  }, [campaign, campaignSetDefault])

  return (
    <div className={classes.root}>
      <Grid container>
        {isEdit && priority === 'default' && !campaign?.isDefault && (
          <Grid container alignItems='center' className={mainClasses.ctnTitle}>
            <Grid container item justifyContent='flex-end' xs={12}>
              <ActionButton
                variant='primary'
                dataTest='button-push-live'
                onClick={handlePushLive}
                isWorking={campaignSetDefault.isWorking}
              >Push Live
              </ActionButton>
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          <FormInputRow
            title='Campaign Name'
            description='This is the name of the campaign to be used internally only, the users cannot see this information.'
            autofocus
            smallFormTitles
            InputComponent={
              <Input
                label='Campaign Name'
                name='name'
                control={form.control}
                rules={{ required: true }}
                valid={errors.name === undefined}
                error='Campaign Name is required'
                showErrors
              />
            }
          />
        </Grid>
        <Grid item xs={12}>
          <FormInputRow
            title='Campaign Priority'
            description='Select this campaign as default if you want it to appear when no campaign is scheduled or schedule the campaign.'
            smallFormTitles
            InputComponent={
              <Controller
                name='priority'
                control={form.control}
                defaultValue='default'
                render={({ field }) => {
                  return (
                    <FormControl variant='outlined' className={classes.root}>
                      <RadioGroup aria-label='Campaign Priority' {...field} onChange={ev => handleChangePriority(ev, field)}>
                        <Grid container>
                          <Grid item xs={6}>
                            <FormControlLabel value='default' control={<Radio color='primary' />} label='Use this campaign as default' />
                          </Grid>
                          <Grid item xs={6}>
                            <FormControlLabel value='scheduled' control={<Radio color='primary' />} label='Schedule this campaign' />
                          </Grid>
                        </Grid>
                      </RadioGroup>
                    </FormControl>
                  )
                }}
              />
            }
          />
        </Grid>

        {priority === 'scheduled' && (
          <>
            <Grid item xs={12}>
              <FormInputRow
                title='Campaign Scheduling'
                description='Select the date that this campaign must be pushed to live and the date that is should be removed from live. The changes will be made at 00:00:00 GMT'
                autofocus
                smallFormTitles
                InputComponent={
                  <Grid container>
                    <Grid item xs={6} className={classes.startDate}>
                      <DateInput
                        control={form.control}
                        name='startDate'
                        label='Start Date'
                        rules={{ required: true }}
                        valid={errors.startDate === undefined}
                        error='Start Date is required'
                        showErrors
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <DateInput
                        control={form.control}
                        name='endDate'
                        label='End Date'
                        disabled={!startDate}
                        minDate={minEndDate}
                        rules={{
                          required: {
                            value: true,
                            message: 'End Date is required'
                          },
                          validate: {
                            startDate: v => {
                              if (!v) return true
                              const startDate = parseISO(getValues('startDate'))
                              const endDate = parseISO(v)

                              return isAfter(endDate, startDate) ? true : 'End Date should be after Start Date'
                            }
                          }
                        }}
                        valid={errors.endDate === undefined}
                        error={errors?.endDate?.message}
                        showErrors
                      />
                    </Grid>
                  </Grid>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputRow
                title='Audience'
                description='Select the audience in which this campaign should be targeted to. You can select as many audiences as you want.'
                autofocus
                smallFormTitles
                InputComponent={
                  <Controller
                    name='audience'
                    control={form.control}
                    rules={{ required: true }}
                    render={({ field }) =>
                      <FormControl error={errors.audience !== undefined} variant='outlined' className={classes.selectControl}>
                        <InputLabel id='audience-select-lbl'>Audience for this Campaign</InputLabel>
                        <Select
                          variant='outlined'
                          {...field}
                          id='audience-select'
                          labelId='audience-select-lbl'
                          value={field.value || ''}
                        >
                          <MenuItem value=''><em>None</em></MenuItem>
                          {AudienceOptions.map(opt => <MenuItem value={opt.value} key={opt.value}>{opt.label}</MenuItem>)}
                        </Select>
                        {errors.audience !== undefined && (
                          <FormHelperText>Audience is required</FormHelperText>
                        )}
                      </FormControl>}
                  />
                }
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <FormInputRow
            title='Campaign Landing Page'
            description='This is the link to the campaign landing page that the user will be redirected to by clicking on the banners CTAs.'
            smallFormTitles
            InputComponent={
              <Input
                label='Landing Page Link'
                name='landingPage'
                control={form.control}
                rules={{ required: true }}
                valid={errors.landingPage === undefined}
                error='Campaign Landing Page is required'
                showErrors
              />
            }
          />
        </Grid>
      </Grid>
    </div>
  )
}

export default DetailTab
